import type { ApiResponse, BrandSection, CommunityReport, CommunityReportFields, CommunitySlotDetail, FlashSession, GuideCatalogItem, GuideCategory, GuideProductFields, GuideSlotDetail, Product, Report } from '../types'
import { fetchJson } from './fetch-json'
import { collectReportsInBackground, readReportJob } from './report-job-client'

async function boundedRequest<T>(path: string, sync = false): Promise<T> {
  const body = await fetchJson<ApiResponse<T>>(path, {
    method: sync ? 'POST' : 'GET', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, ...(sync ? { body: '{}' } : {}),
  }, sync ? 55_000 : 15_000, sync ? '等待采集结果超时，请稍后刷新确认结果后再重试' : '列表读取超时，请刷新重试')
  if (!body.success) throw new Error(body.error || '操作失败，请稍后重试')
  return body.data as T
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    credentials: 'include',
  })
  const body = await response.json() as ApiResponse<T>
  if (!response.ok || !body.success) throw new Error(body.error || '操作失败，请稍后重试')
  return body.data as T
}

async function requestForm<T>(path:string,form:FormData):Promise<T>{
  const response=await fetch(path,{method:'POST',body:form,credentials:'include'})
  const body=await response.json() as ApiResponse<T>
  if(!response.ok||!body.success)throw new Error(body.error||'上传失败，请稍后重试')
  return body.data as T
}

export const api = {
  brands: () => request<BrandSection[]>('/api/brands'),
  sessions: () => request<FlashSession[]>('/api/flash-sessions'),
  reports: () => boundedRequest<Report[]>('/api/reports'),
  products: () => request<Product[]>('/api/products'),
  worthProducts: () => request<Product[]>('/api/worth-products'),
  session: () => request<{ authenticated: boolean }>('/api/session'),
  settings: () => request<Record<string,string>>('/api/settings'),
  login: (password: string) => request<{ authenticated: boolean }>('/api/login', { method: 'POST', body: JSON.stringify({ password }) }),
  logout: () => request<{ authenticated: boolean }>('/api/logout', { method: 'POST' }),
  saveProduct: (product: Product) => request<Product>(`/api/products/${product.id}`, { method: 'PUT', body: JSON.stringify(product) }),
  createProduct: (product: Partial<Product>) => request<Product>('/api/products', { method: 'POST', body: JSON.stringify(product) }),
  duplicateProduct: (id: number) => request<Product>(`/api/products/${id}/duplicate`, { method: 'POST' }),
  deleteProduct: (id: number) => request<{ id: number }>(`/api/products/${id}`, { method: 'DELETE' }),
  brandsAdmin: {
    create: (data: Omit<BrandSection,'id'|'products'>) => request('/api/brands',{method:'POST',body:JSON.stringify(data)}),
    update: (id:number,data: Omit<BrandSection,'id'|'products'>) => request(`/api/brands/${id}`,{method:'PUT',body:JSON.stringify(data)}),
    delete: (id:number) => request(`/api/brands/${id}`,{method:'DELETE'}),
  },
  sessionsAdmin: {
    create: (data: Omit<FlashSession,'id'|'products'>) => request('/api/flash-sessions',{method:'POST',body:JSON.stringify(data)}),
    update: (id:number,data: Omit<FlashSession,'id'|'products'>) => request(`/api/flash-sessions/${id}`,{method:'PUT',body:JSON.stringify(data)}),
    delete: (id:number) => request(`/api/flash-sessions/${id}`,{method:'DELETE'}),
  },
  exportData: () => request<Record<string,unknown>>('/api/backup/export'),
  importData: (data: unknown) => request('/api/backup/import',{method:'POST',body:JSON.stringify(data)}),
  resetData: () => request('/api/backup/reset',{method:'POST'}),
  saveSetting: (key:string,value:string) => request(`/api/settings/${key}`,{method:'PUT',body:JSON.stringify({value})}),
  reportsAdmin: {
    list: () => request<{items:Report[];latestSync:Record<string,unknown>|null}>('/api/reports/admin'),
    create: (data:Partial<Report>) => request<Report>('/api/reports',{method:'POST',body:JSON.stringify(data)}),
    update: (id:number,data:Report) => request<Report>(`/api/reports/${id}`,{method:'PUT',body:JSON.stringify(data)}),
    delete: (id:number) => request(`/api/reports/${id}`,{method:'DELETE'}),
    sync: collectReportsInBackground,
    syncStatus: readReportJob,
  },
  guide: {
    sync: () => request<{runId:string;status:'success'|'partial'|'failed';count:number;errors:string[]}>('/api/guide/cloud-sync',{method:'POST'}),
    latestSync: () => request<{status:string;completed_at:string|null;uploaded_count:number;error_summary:string|null}|null>('/api/guide/cloud-sync/latest'),
    categories: () => request<GuideCategory[]>('/api/guide/categories'),
    catalog: (category:string,query='') => request<GuideCatalogItem[]>(`/api/guide/catalog?${new URLSearchParams({category,...(query.trim()?{q:query.trim()}: {})})}`),
    slot: (slotId:string) => request<GuideSlotDetail>(`/api/guide/slots/${encodeURIComponent(slotId)}`),
    save: (slotId:string,data:GuideProductFields) => request(`/api/guide/slots/${encodeURIComponent(slotId)}/override`,{method:'PUT',body:JSON.stringify(data)}),
    restore: (slotId:string) => request(`/api/guide/slots/${encodeURIComponent(slotId)}/override`,{method:'DELETE'}),
    upload: (file:File) => {const form=new FormData();form.set('file',file);return requestForm<{key:string;url:string}>('/api/guide/images',form)},
  },
  community: {
    reports: (query='') => boundedRequest<CommunityReport[]>(`/api/community/reports${query.trim()?`?${new URLSearchParams({q:query.trim()})}`:''}`),
    slot: (slotId:string) => request<CommunitySlotDetail>(`/api/community/slots/${encodeURIComponent(slotId)}`),
    save: (slotId:string,data:CommunityReportFields) => request(`/api/community/slots/${encodeURIComponent(slotId)}/override`,{method:'PUT',body:JSON.stringify(data)}),
    restore: (slotId:string) => request(`/api/community/slots/${encodeURIComponent(slotId)}/override`,{method:'DELETE'}),
    upload: (file:File) => {const form=new FormData();form.set('file',file);return requestForm<{key:string;url:string}>('/api/community/images',form)},
    sync: () => boundedRequest<{runId:string;count:number;status:'success'}>('/api/community/sync',true),
    latest: () => request<Record<string,unknown>|null>('/api/community/sync/latest'),
  },
}
